import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Helpers
import { isConsumerDomain, validEmail, isEmptyString } from 'Shared/helpers';

// Components
import { WpModal, WpModalBody, WpModalFooter, WpModalHeader } from 'Wpds/components/modal';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import { WpFormLayout } from 'Wpds/components/form';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpIcon from 'Wpds/components/icon/WpIcon';

type Props = {
  isOpen: boolean;
  teammates: {};
  toggleOpen(): void;
  handleShareAgenda: Function;
  handleShareLater: Function;
};

const ShareAgendaModal = ({ isOpen, teammates, toggleOpen, handleShareAgenda, handleShareLater }: Props) => {
  const [isSubmitting, toggleIsSubmitting] = useToggle(false);
  const [teammatesToInvite, setTeammatesToInvite] = useState({});

  const validateEmailInput = value => {
    if (isEmptyString(value)) return null;
    const isValidEmail = validEmail(value);
    const consumerDomain = isConsumerDomain(value);
    if (isValidEmail && !consumerDomain) return null;
    return 'Please enter a valid work email';
  };

  const checkForError = () => {
    const checkedTeammatesToInvite: any = {};
    Object.keys(teammatesToInvite).forEach(key => {
      checkedTeammatesToInvite[key] = {
        ...teammatesToInvite[key],
        errorMessage: validateEmailInput(teammatesToInvite[key]['email']),
      };
    });
    if (Object.values(checkedTeammatesToInvite).find(({ errorMessage }) => errorMessage)) {
      setTeammatesToInvite(checkedTeammatesToInvite);
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!checkForError()) return;
    toggleIsSubmitting();
    const validTeammatesToInvite = Object.values(teammatesToInvite).filter(info => info['fullName'] || info['email']);
    handleShareAgenda(validTeammatesToInvite).then(() => {
      toggleIsSubmitting();
    });
  };

  const handleInviteLater = () => {
    if (!checkForError()) return;
    const validTeammatesToInvite = Object.values(teammatesToInvite).filter(info => info['fullName'] || info['email']);
    handleShareLater(validTeammatesToInvite);
  };

  useEffect(() => {
    setTeammatesToInvite(teammates);
  }, []);

  const handleAddTeammate = () => {
    const newUuid = uuidv4();
    const newTeammate = { [newUuid]: { fullName: '', email: '', uuid: newUuid, errorMessage: null } };
    setTeammatesToInvite({ ...teammatesToInvite, ...newTeammate });
  };

  const handleNameInputChange = (newValue, id) => {
    const updateTeammate = { ...teammatesToInvite[id], fullName: newValue };
    setTeammatesToInvite({ ...teammatesToInvite, [id]: updateTeammate });
  };

  const handleEmailInputChange = (newValue, id) => {
    const updateTeammate = { ...teammatesToInvite[id], email: newValue };
    setTeammatesToInvite({ ...teammatesToInvite, [id]: updateTeammate });
  };

  return (
    <WpModal isOpen={isOpen} onToggle={toggleOpen} size="md" dismissOnBackdrop={false}>
      <WpModalHeader onToggle={toggleOpen}>What&apos;s Next?</WpModalHeader>
      <WpModalBody>
        <p>
          Now that you&apos;ve set up your meeting workspaces, share them with your teammate(s) to start collaborating.
        </p>
        <WpFormLayout direction="vertical">
          {Object.values(teammatesToInvite).map((teammate, index) => {
            return (
              <WpFormLayout direction="horizontal">
                <WpTextField
                  id={`name_input_${index}`}
                  label="Name"
                  labelHidden={index > 0}
                  value={teammate['fullName']}
                  onChange={newValue => handleNameInputChange(newValue, teammate['uuid'])}
                />
                <WpTextField
                  id={`email_input_${index}`}
                  label="Email"
                  value={teammate['email']}
                  labelHidden={index > 0}
                  disabled={isSubmitting}
                  showRequiredIndicator={false}
                  onChange={newValue => handleEmailInputChange(newValue, teammate['uuid'])}
                  error={teammate['errorMessage']}
                />
              </WpFormLayout>
            );
          })}
          {Object.keys(teammates).length == 1 && (
            <WpButton variant="secondary" onClick={handleAddTeammate}>
              <WpIcon name="plus-circle" /> add another teammate
            </WpButton>
          )}
        </WpFormLayout>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton
            variant="tertiary"
            size="lg"
            disabled={isSubmitting}
            onClick={handleInviteLater}
            qaTest="share-agenda-modal-share-later"
          >
            Save and invite later
          </WpButton>
          <WpButton
            onClick={handleSubmit}
            size="lg"
            btnType="submit"
            loading={isSubmitting}
            qaTest="share-agenda-modal-submit"
            disabled={isSubmitting}
          >
            Invite teammate(s)
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default ShareAgendaModal;
